import { useState, useCallback, useRef, useEffect, type CSSProperties } from 'react';

export interface ChipOption<T extends string | number> {
  value: T;
  label: string;
}

export interface ChipMultiSelectProps<T extends string | number> {
  values: readonly T[];
  onChange: (next: readonly T[]) => void;
  options: readonly ChipOption<T>[];
  placeholder?: string;
  maxValues?: number;
  searchable?: boolean;
  disabled?: boolean;
  label?: string;
}

export function ChipMultiSelect<T extends string | number,>({
  values,
  onChange,
  options,
  placeholder,
  maxValues,
  searchable,
  disabled,
  label,
}: ChipMultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const isSearchable = searchable ?? options.length > 7;
  const addDisabled = maxValues !== undefined && values.length >= maxValues;

  const handleRemove = useCallback(
    (chipValue: T) => {
      onChange(values.filter((v) => v !== chipValue));
    },
    [values, onChange],
  );

  const handleToggleOption = useCallback(
    (option: ChipOption<T>) => {
      const isSelected = values.includes(option.value);
      if (isSelected) {
        onChange(values.filter((v) => v !== option.value));
      } else {
        onChange([...values, option.value]);
      }
    },
    [values, onChange],
  );

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handleMouseDown);
    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open]);

  const filteredOptions =
    isSearchable && query
      ? options.filter((o) =>
          o.label.toLowerCase().includes(query.toLowerCase()),
        )
      : options;

  const rootStyle: CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: 'none', cursor: 'not-allowed' }
    : {};

  const popoverStyle: CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    maxHeight: '280px',
    overflowY: 'auto',
    zIndex: 1000,
    background: 'var(--paper-bg)',
    border: '1px solid var(--paper-rule)',
  };

  return (
    <div ref={containerRef} style={rootStyle}>
      {label && (
        <div
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: 'var(--ink-faint)',
            marginBottom: '6px',
          }}
        >
          {label}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          alignItems: 'center',
        }}
      >
        {values.length === 0 && placeholder && (
          <span
            style={{
              font: "400 12px 'IBM Plex Sans'",
              color: 'var(--ink-mute)',
              fontStyle: 'italic',
            }}
          >
            {placeholder}
          </span>
        )}
        {values.map((v) => {
          const option = options.find((o) => o.value === v);
          if (!option) return null;
          return (
            <span
              key={String(v)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 4px 4px 10px',
                background: 'var(--paper)',
                border: '1px solid var(--rule)',
                font: "500 12px 'IBM Plex Sans'",
              }}
            >
              {option.label}
              <button
                type="button"
                aria-label="Remove"
                onClick={() => handleRemove(v)}
                style={{
                  width: '18px',
                  height: '18px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--ink-mute)',
                  fontSize: '14px',
                  lineHeight: '1',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ×
              </button>
            </span>
          );
        })}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            disabled={addDisabled}
            onClick={() => {
              setOpen((prev) => !prev);
              setQuery('');
            }}
            style={{
              padding: '4px 10px',
              background: 'transparent',
              border: '1px dashed var(--rule)',
              color: 'var(--ink-mute)',
              cursor: addDisabled ? 'not-allowed' : 'pointer',
              font: "500 11px 'JetBrains Mono', monospace",
              letterSpacing: '.08em',
              opacity: addDisabled ? 0.4 : 1,
            }}
          >
            +
          </button>
          {open && (
            <div style={popoverStyle}>
              {isSearchable && (
                <div
                  style={{
                    padding: '8px 10px',
                    borderBottom: '1px solid var(--paper-rule)',
                  }}
                >
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Поиск..."
                    autoFocus
                    style={{
                      width: '100%',
                      border: '1px solid var(--paper-rule)',
                      background: 'transparent',
                      padding: '4px 8px',
                      font: "400 12px 'IBM Plex Sans'",
                      outline: 'none',
                      color: 'inherit',
                    }}
                  />
                </div>
              )}
              {filteredOptions.map((option) => {
                const isSelected = values.includes(option.value);
                return (
                  <div
                    key={String(option.value)}
                    onClick={() => handleToggleOption(option)}
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '8px',
                      padding: '8px 10px',
                      cursor: 'pointer',
                      font: "500 13px 'IBM Plex Sans'",
                      background: isSelected ? 'var(--paper-dim)' : 'transparent',
                    }}
                  >
                    <span style={{ minWidth: '16px', display: 'inline-block' }}>
                      {isSelected ? '✓' : ''}
                    </span>
                    {option.label}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
